const createNewCartItem = (cartObject, variantId = 1) => {
  const newItemId = `${cartObject.productId}-${variantId}`;

  return {
    ...cartObject,
    id: newItemId,
    variantId: variantId,
  };
};

const createNewPersonalizationCartItem = (
  personalizationProduct,
  variantId = 1,
) => {
  const newItemId = `${personalizationProduct.productId}-${variantId}`;

  return {
    ...personalizationProduct,
    id: newItemId,
    variantId: variantId,
  };
};

const getCount = (orders) => {
  const totalCartItems = Object.values(orders).reduce((acc, order) => {
    return acc + order.cartItems.length;
  }, 0);

  return totalCartItems;
};

const getNextVariantId = (cartItems, productId) => {
  let maxVariantId = 0;

  Object.values(cartItems).forEach((cartItem) => {
    if (cartItem.productId === productId) {
      maxVariantId = Math.max(maxVariantId, cartItem.variantId);
    }
  });

  return maxVariantId + 1;
};

const calculateTotalPrice = (orders) => {
  let total = 0;

  for (let studentId in orders) {
    let studentOrder = orders[studentId];

    if (Array.isArray(studentOrder.cartItems)) {
      // Handle cartItems as array
      studentOrder.cartItems.forEach((cartItem) => {
        let itemPrice = determineCartItemPrice(cartItem);
        total += itemPrice * cartItem.quantity;
      });
    } else {
      // Handle cartItems as object (current structure)
      for (let cartItemId in studentOrder.cartItems) {
        let cartItem = studentOrder.cartItems[cartItemId];
        let itemPrice = determineCartItemPrice(cartItem);
        total += itemPrice * cartItem.quantity;
      }
    }
  }

  return total;
};

const sumUniqueBackgroundPrices = (orders) => {
  let total = 0;

  for (let studentId in orders) {
    const studentOrder = orders[studentId];
    const uniqueBackgroundIds = new Set(); // To track unique background IDs
    let singleChargeApplied = false; // Flag to track if the single charge has been applied globally

    const chargeForYpxBackgroundUpgrades =
      studentOrder.event.chargeForYpxBackgroundUpgrades;

    for (let cartItemId in studentOrder.cartItems) {
      const cartItem = studentOrder.cartItems[cartItemId];

      // Check if backgroundSelections key exists and it's an array
      if (Array.isArray(cartItem.backgroundSelections)) {
        for (const selection of cartItem.backgroundSelections) {
          let price = parseInt(selection.price, 10); // Ensure price is an integer

          // Only proceed if the price is greater than 0
          if (price > 0) {
            if (chargeForYpxBackgroundUpgrades === false && cartItem.isYPX) {
              continue;
            }

            // If single charge is enabled globally and we've already applied it, skip further additions
            if (
              selection.single_charge_for_background_upgrades &&
              singleChargeApplied
            ) {
              continue;
            }

            // If we haven't encountered this background ID before, add its price to the total
            if (!uniqueBackgroundIds.has(selection.id)) {
              total += price;
              uniqueBackgroundIds.add(selection.id); // Mark this ID as encountered

              // If single charge is enabled, set the flag to true after the first addition
              if (selection.single_charge_for_background_upgrades) {
                singleChargeApplied = true;
              }
            }
          }
        }
      }
    }
  }

  return total;
};

const allOrdersAreEmpty = (orders) => {
  return Object.values(orders).every((order) => order.cartItems.length === 0);
};

const orderHasNoRetouching = (cartItems) => {
  return (
    cartItems?.every((cartItem) => cartItem.isRetouching === false) ?? false
  );
};

const unlockedGroups = (cartItems) => {
  // Check if cartItems is an array, if not, default to an empty array
  let groups = (Array.isArray(cartItems) ? cartItems : [])
    .filter((item) => {
      return item.lockType === 'unlocker' && Array.isArray(item.lockGroup);
    })
    .flatMap((item) => item.lockGroup);

  return [...new Set(groups)];
};

// TODO: flesh this out
const getCartTotal = (orders) => {
  let total = 0;

  for (let orderId in orders) {
    total += getOrderTotal(orders[orderId].cartItems);
  }

  return total;
};

const getOrderTotal = (cartItems) => {
  const orderTotal = cartItems.reduce((total, item) => {
    // Use determineCartItemPrice for all items to include product option prices
    return total + determineCartItemPrice(item) * item.quantity;
  }, 0);

  return orderTotal;
};

const getAllCartSKUs = (orders) => {
  const allSKUsSet = Object.values(orders).reduce((allSKUs, order) => {
    order.cartItems.forEach((item) => {
      allSKUs.add(item.sku);
    });
    return allSKUs;
  }, new Set());

  return Array.from(allSKUsSet);
};

const getAllOrdersTotal = (orders) => {
  return Object.values(orders).reduce((total, order) => {
    return total + getOrderTotal(order.cartItems);
  }, 0);
};

const eligibleIncentivesForOrder = (
  cartItems,
  incentiveProducts,
  voucherTotalCents = 0,
  hasVouchers = false,
) => {
  // Check for at least one non-incentive product in the cart (vouchers count as non-incentive)
  const hasNonIncentiveProduct =
    cartItems.some((item) => !item.isIncentive) || hasVouchers;

  // If no non-incentive product is found, return an empty array
  if (!hasNonIncentiveProduct) {
    return [];
  }

  // Include voucher total (converted from cents to dollars) in the order total
  const orderTotal = getOrderTotal(cartItems) + voucherTotalCents / 100;

  const eligibleIncentives = incentiveProducts.filter((incentiveProduct) => {
    // Check if order total is greater than incentive product's threshold
    if (
      orderTotal >= incentiveProduct.incentive_threshold &&
      incentiveProduct.incentive_applies_to !== 'cart_level'
    ) {
      // Check if incentive product is not yet in the user's cart
      const isInCart = cartItems.some(
        (cartItem) => cartItem.productId === incentiveProduct.id,
      );
      return !isInCart;
    }
    return false;
  });

  return eligibleIncentives;
};

const eligibleIncentivesForCart = (
  orders,
  incentiveProducts,
  voucherTotalCents = 0,
) => {
  // Include voucher total (converted from cents to dollars) in the cart total
  const cartTotal = getCartTotal(orders) + voucherTotalCents / 100;

  const eligibleIncentives = incentiveProducts.filter((incentiveProduct) => {
    // Check if order total is greater than incentive product's threshold
    if (
      cartTotal >= incentiveProduct.incentive_threshold &&
      incentiveProduct.incentive_applies_to === 'cart_level'
    ) {
      // Check if incentive product is not yet in any order's cartItems
      // For cart-level items, we just need to check the SKU vs. the product ID, which may be different across
      // different price lists
      let isInCart = false;
      for (let orderId in orders) {
        if (
          orders[orderId].cartItems.some(
            (cartItem) => cartItem.productId === incentiveProduct.id,
          )
        ) {
          isInCart = true;
          break;
        }
      }
      return !isInCart;
    }
    return false;
  });

  return eligibleIncentives;
};

// Sort cart items so that incentives come after non-incentives
const sortCartItems = (cartItems) => {
  return [...cartItems].sort((a, b) => {
    if (a.isIncentive === b.isIncentive) {
      // if both items are the same type (either both incentive or both non-incentive), don't change order
      return 0;
    }
    // if a is incentive and b is not, a should come after b
    if (a.isIncentive && !b.isIncentive) {
      return 1;
    }
    // if b is incentive and a is not, a should come before b
    if (!a.isIncentive && b.isIncentive) {
      return -1;
    }
  });
};

const cartItemProductHasContentsArray = (cartItem) => {
  return (
    cartItem.contents &&
    Array.isArray(cartItem.contents) &&
    cartItem.contents.length > 0
  );
};

const interstitialProductsToPrompt = (
  interstitialProducts,
  interstitialProductsPrompted,
  cartItems = [],
) => {
  const productsToPrompt = interstitialProducts.filter((product) => {
    if (
      product.lock_type !== 'unlockable' ||
      interstitialProductsPrompted.includes(product.id)
    )
      return false;

    return cartItems.some(
      (cartItem) =>
        cartItem.lockType === 'unlocker' &&
        product.lock_group.some((group) => cartItem.lockGroup.includes(group)),
    );
  });

  return productsToPrompt;
};

const nthFreeQualifiersInCart = (cartItems) => {
  const nthFreeQualifiersInCart = cartItems.filter((item) => {
    return item.isNthFreeQualifier === true;
  });

  return nthFreeQualifiersInCart;
};

const nthFreeProductsInCart = (cartItems) => {
  const nthFreeProductsInCart = cartItems.filter((item) => {
    return item.isNthFreeProduct === true;
  });

  return nthFreeProductsInCart;
};

const highestPriceNthFreeQualifier = (cartItems) => {
  const nthFreeQualifiersInCart = cartItems.filter((item) => {
    return item.isNthFreeQualifier === true;
  });

  if (nthFreeQualifiersInCart.length === 0) {
    return 0;
  }

  return nthFreeQualifiersInCart.reduce((highestPrice, currentItem) => {
    return Math.max(highestPrice, currentItem.price);
  }, nthFreeQualifiersInCart[0].price);
};

const lowestPriceNthFreeQualifier = (cartItems) => {
  const nthFreeQualifiersInCart = cartItems.filter((item) => {
    return item.isNthFreeQualifier === true;
  });

  if (nthFreeQualifiersInCart.length === 0) {
    return 0;
  }

  return nthFreeQualifiersInCart.reduce((lowestPrice, currentItem) => {
    return Math.min(lowestPrice, currentItem.price);
  }, nthFreeQualifiersInCart[0].price);
};

const optionValueMatches = (option, selectedValue) =>
  option.key === selectedValue || String(option.id) === String(selectedValue);

const configureCartItem = ({
  backgroundIds,
  backgroundSelections,
  incentiveProductIds,
  productConfig,
  productData,
  poses,
  poseIds,
  personalizationValueForCart,
  personalizationSetToSameValue,
  quantity,
  requiredFields,
  legacyRequiredFields,
  productOptionKey,
  productOptions,
  eventId,
  selectedCropOptions,
  childCropSelections = [],
  blockInternationalShipping = false,
  blockInternationalShippingChild = false,
}) => {
  let cartObject = {};
  cartObject.additionalData = productData.additional_data || {};
  cartObject.backgroundOptionsCount = productData.background_options_count;
  cartObject.productId = productData.id;
  cartObject.productName = productData.name;
  cartObject.quantity = quantity || 1;
  cartObject.personalizationValue = personalizationValueForCart;
  cartObject.price = productData.price;
  cartObject.nthFreeAmountAllowed = productData.nth_free_amount_allowed;
  cartObject.nthFreeProductId = productData.nth_free_product_id;
  cartObject.nthFreeSKU = productData.nth_free_sku;
  cartObject.nthFreeThreshold = productData.nth_free_threshold;
  cartObject.nthFreeValue = productData.nth_free_value;
  cartObject.personalizationStatus = productData.personalization_status;
  cartObject.posesCount = productData.poses_count;
  cartObject.productType = productData.product_type;
  cartObject.contents = productData.contents;
  cartObject.imageThumbnail = productData.image_thumbnail;
  cartObject.imageType = productData.image_type;
  cartObject.isGroup = productData.image_type === 'group_image';
  cartObject.incentiveThreshold = productData.incentive_threshold || null;
  cartObject.includesRetouching = productData.includes_retouching;
  cartObject.isPersonalization = productData.is_personalization;
  cartObject.isPreCartOfferProduct = productData.is_pre_cart_offer || false;
  cartObject.isRetouching = productData.is_retouching;
  cartObject.lockType = productData.lock_type;
  cartObject.lockGroup = productData.lock_group;
  cartObject.isIncentive = incentiveProductIds.includes(productData.id);
  cartObject.backgroundIds = backgroundIds || [];
  cartObject.backgroundSelections = backgroundSelections || [];
  cartObject.requiresShipping = productData.requires_shipping;
  cartObject.blockInternationalShipping = blockInternationalShipping === true;
  // Child-level shipping snapshot for bundles whose parent has
  // requires_shipping=false. `requiresShipping` alone misses selected or
  // required child products that still ship.
  cartObject.blockInternationalShippingChild =
    blockInternationalShippingChild === true;
  cartObject.sku = productData.sku;
  cartObject.eventId = eventId;

  // TODO: update this to use poseIds as passed... need to confirm that YPX/BYO is submitting poseIds
  cartObject.poseIds = poses ? poses.map((p) => p.id) : poseIds || [];

  cartObject.poses = poses || [];
  cartObject.productIds = [];
  cartObject.isBYO = productData.is_byo;
  cartObject.isYPX = productData.is_ypx;
  cartObject.isMultiConfig = productData.is_multi_config;
  cartObject.requiredFields = requiredFields;
  cartObject.legacyRequiredFields = legacyRequiredFields;
  const selectedCrop = poses?.find(
    (pose) => !!pose.selected_crop,
  )?.selected_crop;
  cartObject.selectedCrop = selectedCrop;
  cartObject.selectedCropOptions = selectedCropOptions || [];

  // establish whether product is an nth-free product
  cartObject.isNthFreeProduct = productData.product_type == 'nth_free';

  // Handle product options - support both legacy single option and new multiple options
  if (productOptions && Object.keys(productOptions).length > 0) {
    // New structure - multiple option sets
    const selectedOptions = [];

    // Process each option set selection
    Object.entries(productOptions).forEach(([optionSetKey, optionValue]) => {
      if (optionValue) {
        // Find the option set and selected option
        const optionSet = productData.product_option_sets?.find(
          (set) => set.key === optionSetKey,
        );

        if (optionSet) {
          const selectedOption = optionSet.product_options?.find((opt) =>
            optionValueMatches(opt, optionValue),
          );

          if (selectedOption) {
            selectedOptions.push({
              optionSetId: optionSet.id,
              optionSetKey: optionSet.key,
              optionSetName: optionSet.name,
              optionSetDisplayName: optionSet.display_name,
              optionId: selectedOption.id,
              optionKey: selectedOption.key,
              optionName: selectedOption.name,
              optionSku: selectedOption.sku,
              optionPrice: selectedOption.price || 0,
            });
          }
        }
      }
    });

    cartObject.selectedProductOptions = selectedOptions;

    // For backward compatibility, set the first option as productOption
    if (selectedOptions.length > 0) {
      cartObject.productOption = {
        id: selectedOptions[0].optionId,
        key: selectedOptions[0].optionKey,
        name: selectedOptions[0].optionName,
        sku: selectedOptions[0].optionSku,
        price: selectedOptions[0].optionPrice,
      };
    }
  } else if (productOptionKey) {
    // Legacy support - single option key
    const selectedOption = productData.product_options?.find(
      (option) => option.key === productOptionKey,
    );

    if (selectedOption) {
      cartObject.productOption = selectedOption;
      cartObject.selectedProductOptions = [
        {
          optionSetId: 'legacy',
          optionSetKey: 'legacy',
          optionSetName: 'Product Options',
          optionId: selectedOption.id,
          optionKey: selectedOption.key,
          optionName: selectedOption.name,
          optionSku: selectedOption.sku,
          optionPrice: selectedOption.price || 0,
        },
      ];
    }
  }

  let isNthFreeQualifier = false;

  if (
    productData.nth_free_sku &&
    productData.nth_free_sku !== null &&
    productData.nth_free_sku !== ''
  ) {
    isNthFreeQualifier = true;
  }

  cartObject.isNthFreeQualifier = isNthFreeQualifier;

  if (personalizationValueForCart) {
    // do nothing
  } else {
    // get personalization value for child items from productConfig
    const personalizationValues = Object.values(productConfig).map(
      (p) => p.personalization,
    );
    personalizationValues.reverse();
    cartObject.personalizationValue = personalizationSetToSameValue
      ? personalizationValues.find((p) => !p)
      : null;
  }

  // !productData.is_personalization handles case where personalization producted is added to cart
  // alongside a personalized YPX product
  // TODO: find a better way to handle this
  if (!productData.is_personalization) {
    cartObject.children = Object.values(productConfig).map((child) => {
      const { product } = child;
      if (child.product) cartObject.productIds.push(child.product.id);
      if (child.background) {
        cartObject.backgroundIds.push(child.background.id);
        cartObject.backgroundSelections.push(child.background);
      }

      // Support both single .pose and multiple .poses per slot
      const childPoses = Array.isArray(child.poses)
        ? child.poses
        : child.pose
        ? [child.pose]
        : [];
      childPoses.forEach((p) => cartObject.poseIds.push(p.id));

      // Process product options for this child
      let childSelectedOptions = [];
      if (
        child.productOptions &&
        Object.keys(child.productOptions).length > 0
      ) {
        Object.entries(child.productOptions).forEach(
          ([optionSetKey, optionValue]) => {
            if (optionValue) {
              // Find the option set and selected option from the child product
              const optionSet = product.product_option_sets?.find(
                (set) => set.key === optionSetKey,
              );
              if (optionSet) {
                const selectedOption = optionSet.product_options?.find((opt) =>
                  optionValueMatches(opt, optionValue),
                );
                if (selectedOption) {
                  childSelectedOptions.push({
                    optionSetId: optionSet.id,
                    optionSetKey: optionSet.key,
                    optionSetName: optionSet.display_name || optionSet.name,
                    optionId: selectedOption.id,
                    optionKey: selectedOption.key,
                    optionName: selectedOption.name,
                    optionSku: selectedOption.sku,
                    optionPrice: selectedOption.price || 0,
                  });
                }
              }
            }
          },
        );
      }

      return {
        price: product.price,
        productId: product.id,
        productName: product.name,
        backgrounds: child.background ? [child.background] : [],
        poses: childPoses,
        personalizationStatus: product.personalization_status,
        personalizationValue: child.personalization,
        quantity: 1,
        additionalData: {},
        variantId: null,
        selectedProductOptions: childSelectedOptions,
        selectedCropOptions: Array.isArray(child.cropOptions)
          ? child.cropOptions
          : [],
      };
    });

    // TODO: revisit this logic -- it might be better to handle this via the above 'child' logic
    if (productData.is_multi_config) {
      cartObject.multiConfigChildProducts = productData.child_products.map(
        (child, index) => {
          if (child) cartObject.productIds.push(child.id);

          const pose = cartObject.poses[index];
          const background = cartObject.backgroundSelections[index];

          if (pose) {
            cartObject.poseIds.push(pose.id);
          }

          return {
            price: child.price,
            productId: child.id,
            productName: child.name,
            backgrounds: background ? [background] : [],
            poses: pose ? [pose] : [],
            // personalizationStatus: product.personalization_status, // TODO: do we need to handle personalization?
            // personalizationValue: product.personalization, // TODO: do we need to handle personalization?
            quantity: 1,
            additionalData: {},
            variantId: null,
            selectedCropOptions: childCropSelections[index] || [],
          };
        },
      );
    }
  }

  return cartObject;
};

const cartContainsPersonalizableProducts = (cartItems) => {
  return cartItems.some((cartItem) => {
    // Check the personalization status of the cart item itself
    const isPersonalizable =
      cartItem.personalizationStatus === 'personalization_required' ||
      cartItem.personalizationStatus === 'personalization_optional';

    // Check if there are children and if any of them have a required or optional personalization status
    const hasPersonalizableChildren =
      cartItem.children &&
      cartItem.children.some((childProduct) => {
        return (
          childProduct.personalizationStatus === 'personalization_optional' ||
          childProduct.personalizationStatus === 'personalization_required'
        );
      });

    // Return true if either the item itself or any of its children are personalizable
    return isPersonalizable || hasPersonalizableChildren;
  });
};

const cartContainsNonIncentiveProducts = (cartItems) => {
  return cartItems.some((cartItem) => !cartItem.isIncentive);
};

const determineCartItemPrice = (cartItem) => {
  let basePrice = 0;

  if (cartItem.isBYO) {
    const freeIndices = cartItem?.additionalData?.free_indices || [];

    // Check if cartItem has a 'children' property and that it's an array
    if (Array.isArray(cartItem.children)) {
      // Sum the price of each child, unless its index is in the freeIndices array
      basePrice = cartItem.children.reduce((acc, child, index) => {
        if (freeIndices.includes(index)) {
          return acc;
        }
        let childPrice = child.price || 0;
        // Add child's product option prices
        if (
          child.selectedProductOptions &&
          Array.isArray(child.selectedProductOptions)
        ) {
          const childOptionPrices = child.selectedProductOptions.reduce(
            (optTotal, option) => optTotal + (option.optionPrice || 0),
            0,
          );
          childPrice += childOptionPrices;
        }
        return acc + childPrice;
      }, 0);
    }
  } else if (cartItem.isYPX) {
    // For YPX products, use the parent price and add only the child option prices
    basePrice = cartItem.price || 0;

    // Add only the product option prices from children
    if (Array.isArray(cartItem.children)) {
      const childOptionPrices = cartItem.children.reduce((acc, child) => {
        if (
          child.selectedProductOptions &&
          Array.isArray(child.selectedProductOptions)
        ) {
          const optionTotal = child.selectedProductOptions.reduce(
            (optTotal, option) => optTotal + (option.optionPrice || 0),
            0,
          );
          return acc + optionTotal;
        }
        return acc;
      }, 0);
      basePrice += childOptionPrices;
    }
  } else {
    // Regular product base price
    basePrice = cartItem.price || 0;
  }

  // Add product option prices
  let optionPricesTotal = 0;
  if (
    cartItem.selectedProductOptions &&
    Array.isArray(cartItem.selectedProductOptions)
  ) {
    optionPricesTotal = cartItem.selectedProductOptions.reduce(
      (total, option) => {
        return total + (option.optionPrice || 0);
      },
      0,
    );
  } else if (cartItem.productOption && cartItem.productOption.price) {
    // Legacy single option support
    optionPricesTotal = cartItem.productOption.price;
  }

  return basePrice + optionPricesTotal;
};

const cartItemsIncludeRetouching = (cartItems) => {
  return cartItems?.some((cartItem) => cartItem?.includesRetouching === true);
};

const cartItemsContainRetouchingProduct = (cartItems) => {
  return cartItems?.some((cartItem) => cartItem?.isRetouching === true);
};

const applyDiscountCodeToCart = (currentState, discountCode) => {
  const discountCodes = currentState.discountCodes || [];

  if (
    discountCodes.some(
      (code) => code.discount_code_id === discountCode.discount_code_id,
    )
  ) {
    return currentState;
  }

  return {
    ...currentState,
    discountCodes: [...discountCodes, discountCode],
  };
};

const cartItemsAreGroupProductsOnly = (cartItems) => {
  return cartItems.every((cartItem) => cartItem.isGroup === true);
};

const cartContainsPrepayOrders = (orders) => {
  const isPrepay = Object.values(orders).some((order) => {
    return order.isPrepay === true;
  });

  return isPrepay;
};

const cartContainsPostEventOrders = (orders) => {
  const isPostEvent = Object.values(orders).some((order) => {
    return order.isPrepay === false;
  });

  return isPostEvent;
};

const cartContainsShippingRequiredProducts = (orders) => {
  const hasShippingRequiredCartItems = Object.values(orders).some((order) => {
    return order.cartItems.some((item) => item?.requiresShipping === true);
  });

  return hasShippingRequiredCartItems;
};

// Whether a cart item ships from the parent's region — the cart-time
// mirror of isProductAllowed's leaf/YPX/multi-config branching:
//
//   - Leaf: parent's own requiresShipping is authoritative.
//   - YPX (non-multi-config parent): children decide. The cart item's
//     blockInternationalShippingChild reflects the selected children's
//     ship-flag (set at add-to-cart time); the parent's coarse
//     requiresShipping is often true even when an all-digital selection
//     was made and so must be ignored.
//   - Multi-config: children are required and not substitutable, so EITHER
//     the parent's requires_shipping OR any child's must block (matches
//     isProductAllowed's two-clause check).
const cartItemShipsForRestriction = (item) => {
  if (!item) return false;
  const isMultiConfig = item.isMultiConfig === true;
  const isYpxParent =
    (item.productType === 'parent_product' || item.isYPX === true) &&
    !isMultiConfig;

  if (isMultiConfig) {
    return (
      item.requiresShipping === true ||
      item.blockInternationalShippingChild === true
    );
  }
  if (isYpxParent) {
    return item.blockInternationalShippingChild === true;
  }
  return item.requiresShipping === true;
};

const internationalShippingViolations = (orders, country) => {
  if (!country || country === 'US') return [];
  return Object.values(orders || {}).flatMap((order) =>
    (order?.cartItems || []).filter(
      (item) =>
        item?.blockInternationalShipping === true &&
        cartItemShipsForRestriction(item),
    ),
  );
};

// Returns [{ orderKey, cartItemId }] for incentive cart items the parent
// can't use. Two cases:
//   - Shippable incentives: same as cartItemShipsForRestriction — YPX-aware,
//     scoped to isIncentive=true items only.
//   - Free-shipping incentives: identified by matching productId against the
//     incentive product list (the "Free Shipping" product is typically
//     modeled with requires_shipping=false, so we can't rely on that flag).
//     `incentiveProducts` is optional — when omitted, only the first case
//     is detected. Used by Cart.jsx to auto-remove these on cart mount.
const removableShippingIncentives = (orders, country, incentiveProducts) => {
  if (!country || country === 'US') return [];
  const freeShippingProductIds = new Set(
    (Array.isArray(incentiveProducts) ? incentiveProducts : [])
      .filter((p) => p?.is_free_shipping === true)
      .map((p) => p?.id),
  );
  return Object.entries(orders || {}).flatMap(([orderKey, order]) =>
    (order?.cartItems || [])
      .filter(
        (item) =>
          item?.isIncentive === true &&
          item?.blockInternationalShipping === true &&
          (cartItemShipsForRestriction(item) ||
            freeShippingProductIds.has(item?.productId)),
      )
      .map((item) => ({ orderKey, cartItemId: item.id })),
  );
};

const hasLockedProductsNoUnlockers = (cartItems) => {
  // Check for undefined, null, or non-array input and return false
  if (!Array.isArray(cartItems)) {
    return false;
  }

  // Check if there is at least one item with lockType === 'unlockable'
  const hasUnlockable = cartItems.some(
    (item) => item && item.lockType === 'unlockable',
  );

  // Check if there are no items with lockType === 'unlocker'
  const hasNoUnlocker = cartItems.every(
    (item) => item && item.lockType !== 'unlocker',
  );

  // Return true if both conditions are met, otherwise return false
  return hasUnlockable && hasNoUnlocker;
};

const lockedProductIds = (cartItems) => {
  // Check for undefined, null, or non-array input and return an empty array
  if (!Array.isArray(cartItems)) {
    return [];
  }

  const lockedIds = cartItems
    .filter((item) => {
      return item && item.lockType === 'unlockable';
    })
    .map((item) => item.id);

  return lockedIds;
};

const preCartOfferProductIds = (cartItems) => {
  return cartItems
    .filter((item) => item.isPreCartOfferProduct)
    .map((item) => item.id);
};

const productIdsWithLockedBackgrounds = (cartItems, unlockedGroups) => {
  if (!Array.isArray(cartItems)) {
    return [];
  }

  const productIds = cartItems
    .filter(({ backgroundSelections }) =>
      backgroundSelections.some(
        ({ lock_type, lock_group }) =>
          lock_type === 'unlockable' &&
          !unlockedGroups.some((set) => lock_group.includes(set)),
      ),
    )
    .map((item) => item.id);

  return productIds;
};

const productBackgroundLockGroup = (cartItems) => {};

const preCartOfferProductsToPrompt = (
  cartObject,
  preCartOfferProducts,
  triggersPreCartOfferProducts,
) => {
  // Return empty array if any argument is falsy or not an array where expected
  if (
    !cartObject ||
    !Array.isArray(preCartOfferProducts) ||
    !Array.isArray(triggersPreCartOfferProducts) ||
    cartObject.cartItems?.every(
      (item) => item.isPersonalization || item.isRetouching,
    )
  ) {
    return [];
  }

  // Ensure cartItems is an array, then map over it, default to an empty array if cartItems is undefined/null
  // Look through 'children' to handle YPX/BYO products
  const cartProductIds = Array.isArray(cartObject.cartItems)
    ? cartObject.cartItems.flatMap((item) => {
        // Get the productId of the current item
        const productIds = [item.productId];

        // If the item has children, add their productIds to the array
        if (Array.isArray(item.children)) {
          item.children.forEach((child) => {
            if (child.productId) {
              productIds.push(child.productId);
            }
          });
        }

        return productIds;
      })
    : [];

  // Check if any of the triggersPreCartOfferProducts are in the cart
  const isTriggerProductInCart = triggersPreCartOfferProducts.some((product) =>
    cartProductIds.includes(product.id),
  );

  // Use empty array as a default value for preCartOfferProductsPrompted
  const preCartOfferProductsPromptedIds =
    cartObject.preCartOfferProductsPrompted || [];

  // If a trigger product is in the cart, return empty array
  if (isTriggerProductInCart) {
    return [];
  }

  // Filter preCartOfferProducts to include products not yet prompted and not in the cart
  return preCartOfferProducts.filter(
    (product) =>
      !cartProductIds.includes(product.id) &&
      !preCartOfferProductsPromptedIds.includes(product.id),
  );
};

const cartHasShippingCost = (cartTotalsObject) =>
  cartTotalsObject &&
  typeof cartTotalsObject.shipping === 'number' &&
  cartTotalsObject.shipping > 0;

// Determine whether the order contains a single background upgrade and return true if the studio has set the single_charge_for_background_upgrades flag
const orderContainsSingleBackgroundUpgrade = (
  orders,
  studentId,
  chargeForYpxBackgroundUpgrades = false,
) => {
  if (orders && orders[studentId]) {
    const cartItems = orders[studentId].cartItems;

    for (const cartItem of cartItems) {
      if (cartItem.backgroundSelections) {
        for (const selection of cartItem.backgroundSelections) {
          if (selection.price && parseInt(selection.price) > 0) {
            if (
              cartItem?.isYPX === true &&
              chargeForYpxBackgroundUpgrades === false
            ) {
              return false;
            } else {
              if (selection.single_charge_for_background_upgrades) {
                return true;
              }
            }
          }
        }
      }
    }
  }

  return false;
};

const anyOrderContainsOnlyRetouching = (orders) => {
  // Check if ANY order contains only retouching products
  for (let studentId in orders) {
    const order = orders[studentId];

    // If the order has no items, skip it
    if (!order.cartItems || order.cartItems.length === 0) {
      continue;
    }

    // Check if ALL items in this order are retouching
    const allItemsAreRetouching = order.cartItems.every(
      (cartItem) => cartItem.isRetouching === true,
    );

    // If we found an order with only retouching items, return true
    if (allItemsAreRetouching) {
      return true;
    }
  }

  // No orders contain only retouching products
  return false;
};

export default {
  allOrdersAreEmpty,
  anyOrderContainsOnlyRetouching,
  applyDiscountCodeToCart,
  calculateTotalPrice,
  cartContainsNonIncentiveProducts,
  cartContainsPersonalizableProducts,
  cartContainsPostEventOrders,
  cartContainsPrepayOrders,
  cartContainsShippingRequiredProducts,
  internationalShippingViolations,
  removableShippingIncentives,
  cartHasShippingCost,
  cartItemsAreGroupProductsOnly,
  cartItemsContainRetouchingProduct,
  cartItemsIncludeRetouching,
  cartItemProductHasContentsArray,
  configureCartItem,
  createNewCartItem,
  createNewPersonalizationCartItem,
  determineCartItemPrice,
  eligibleIncentivesForCart,
  eligibleIncentivesForOrder,
  getAllCartSKUs,
  getAllOrdersTotal,
  getCount,
  getNextVariantId,
  getOrderTotal,
  hasLockedProductsNoUnlockers,
  highestPriceNthFreeQualifier,
  lowestPriceNthFreeQualifier,
  interstitialProductsToPrompt,
  lockedProductIds,
  nthFreeProductsInCart,
  nthFreeQualifiersInCart,
  orderHasNoRetouching,
  orderContainsSingleBackgroundUpgrade,
  preCartOfferProductsToPrompt,
  preCartOfferProductIds,
  productIdsWithLockedBackgrounds,
  sortCartItems,
  sumUniqueBackgroundPrices,
  unlockedGroups,
};
